import { ArrowDown, ArrowsClockwise } from '@phosphor-icons/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { syncNow } from '@/core/sync';
import { cx } from './components';
import { isDragging } from './dnd';
import { toast } from './overlays';

/** Swipe in from the left screen edge (phones) to open the drawer. */
export function useEdgeSwipe(onOpen: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    let start: { x: number; y: number; t: number } | null = null;
    const down = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      // Generous edge (Android's own back gesture lives in the outer few px).
      start = t.clientX < 36 ? { x: t.clientX, y: t.clientY, t: Date.now() } : null;
    };
    const move = (e: TouchEvent) => {
      if (!start) return;
      const t = e.touches[0]!;
      const dx = t.clientX - start.x;
      const dy = Math.abs(t.clientY - start.y);
      if (dy > 40) start = null;
      else if (dx > 50) {
        start = null;
        onOpen();
      }
    };
    const up = () => {
      start = null;
    };
    document.addEventListener('touchstart', down, { passive: true });
    document.addEventListener('touchmove', move, { passive: true });
    document.addEventListener('touchend', up, { passive: true });
    return () => {
      document.removeEventListener('touchstart', down);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', up);
    };
  }, [onOpen, enabled]);
}

/**
 * The phone drawer follows the finger: drag from the left edge to pull it open, or drag it (or
 * the dimmed page) left to push it shut. On release it settles by where it is and how fast it
 * was moving, so a quick flick works as well as a slow drag.
 */
export function useDrawerDrag({
  enabled,
  open,
  setOpen,
  drawer,
  scrim,
  side = 'left',
}: {
  enabled: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  drawer: React.RefObject<HTMLElement | null>;
  scrim: React.RefObject<HTMLElement | null>;
  /** which screen edge it slides in from */
  side?: 'left' | 'right';
}) {
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (!enabled) return;
    type Drag = {
      x0: number;
      y0: number;
      from: number;
      width: number;
      pos: number;
      active: boolean;
      /** started at the screen edge with the drawer closed: the touch is the drawer's alone */
      edge: boolean;
      samples: { x: number; t: number }[];
    };
    let d: Drag | null = null;
    const paint = (pos: number) => {
      const el = drawer.current;
      if (!el || !d) return;
      el.style.transition = 'none';
      el.style.visibility = 'visible';
      el.style.transform = `translateX(${side === 'left' ? pos - d.width : d.width - pos}px)`;
      const s = scrim.current;
      if (s) {
        s.style.transition = 'none';
        s.style.opacity = String(pos / d.width);
        s.style.visibility = 'visible';
      }
    };
    const release = () => {
      for (const el of [drawer.current, scrim.current]) {
        if (!el) continue;
        el.style.transition = '';
        el.style.transform = '';
        el.style.visibility = '';
        el.style.opacity = '';
      }
    };
    const down = (e: TouchEvent) => {
      const t = e.touches[0];
      const el = drawer.current;
      if (!t || !el || e.touches.length > 1) return;
      const width = el.getBoundingClientRect().width || 300;
      const target = e.target as Node;
      const startsOnDrawer =
        openRef.current && (el.contains(target) || scrim.current?.contains(target));
      // Closed: a generous edge (Android's back gesture lives in the outer few px).
      const fromEdge = side === 'left' ? t.clientX : window.innerWidth - t.clientX;
      if (!openRef.current && fromEdge > 28) return;
      if (openRef.current && !startsOnDrawer) return;
      const from = openRef.current ? width : 0;
      d = {
        x0: t.clientX,
        y0: t.clientY,
        from,
        width,
        pos: from,
        active: false,
        edge: !openRef.current,
        samples: [],
      };
    };
    const move = (e: TouchEvent) => {
      if (!d) return;
      // Something is being dragged out of the drawer: that's not a swipe.
      if (isDragging()) {
        d = null;
        return;
      }
      const t = e.touches[0]!;
      // Positive = towards open.
      const dx = (t.clientX - d.x0) * (side === 'left' ? 1 : -1);
      const dy = t.clientY - d.y0;
      // From the edge, nothing underneath scrolls (once a scroll starts it can't be stopped).
      if (d.edge && e.cancelable) e.preventDefault();
      if (!d.active) {
        // Wait until it's clearly sideways; a vertical move is a scroll.
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          if (!d.edge) d = null;
          return;
        }
        if (Math.abs(dx) < 8) return;
        d.active = true;
      }
      e.preventDefault();
      d.pos = Math.max(0, Math.min(d.width, d.from + dx));
      d.samples.push({ x: t.clientX, t: e.timeStamp });
      if (d.samples.length > 5) d.samples.shift();
      paint(d.pos);
    };
    const up = () => {
      const cur = d;
      d = null;
      if (!cur?.active) return;
      const a = cur.samples[0];
      const b = cur.samples[cur.samples.length - 1];
      const v = (a && b && b.t > a.t ? (b.x - a.x) / (b.t - a.t) : 0) * (side === 'left' ? 1 : -1);
      // A flick decides on its own; otherwise past halfway stays open.
      const shouldOpen = Math.abs(v) > 0.3 ? v > 0 : cur.pos > cur.width / 2;
      release();
      setOpen(shouldOpen);
    };
    document.addEventListener('touchstart', down, { passive: true });
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', up, { passive: true });
    document.addEventListener('touchcancel', up, { passive: true });
    return () => {
      document.removeEventListener('touchstart', down);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', up);
      document.removeEventListener('touchcancel', up);
    };
  }, [enabled, setOpen, drawer, scrim, side]);
}

/**
 * A scroll container with pull-to-refresh on touch. Pulling down at the top syncs
 * (and runs `onRefresh` if given). Everything else syncs on its own anyway.
 */
export function PullToRefresh({
  children,
  onRefresh,
  className,
  style,
  scrollRef,
}: {
  children: ReactNode;
  onRefresh?: () => Promise<unknown> | void;
  className?: string;
  style?: React.CSSProperties;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const own = useRef<HTMLDivElement>(null);
  const ref = scrollRef ?? own;
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  const THRESHOLD = 72;

  const onTouchStart = (e: React.TouchEvent) => {
    if (busy) return;
    start.current = (ref.current?.scrollTop ?? 0) <= 0 ? e.touches[0]!.clientY : null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (start.current === null) return;
    const dy = e.touches[0]!.clientY - start.current;
    if (dy <= 0) {
      setPull(0);
      return;
    }
    // Resistance so it feels elastic.
    setPull(Math.min(120, dy * 0.5));
  };
  const onTouchEnd = async () => {
    if (start.current === null) return;
    start.current = null;
    if (pull >= THRESHOLD * 0.5 + 10) {
      setBusy(true);
      setPull(48);
      try {
        await Promise.all([syncNow(), onRefresh?.()]);
        toast('Synced');
      } finally {
        setBusy(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  };

  return (
    <div
      ref={ref}
      className={cx('scroll', className)}
      style={style}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="e-ptr"
        style={{ height: pull, transition: start.current === null ? 'height 200ms' : 'none' }}
      >
        <div style={{ padding: 12 }}>
          {busy ? (
            <ArrowsClockwise size={22} style={{ animation: 'e-spin 0.8s linear infinite' }} />
          ) : (
            <ArrowDown
              size={22}
              style={{
                transform: `rotate(${pull > 46 ? 180 : 0}deg)`,
                transition: 'transform 150ms',
              }}
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

/** Global keyboard shortcut; `combo` like "mod+k", "mod+shift+n", "alt+1". */
export function useHotkey(combo: string, fn: (e: KeyboardEvent) => void, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!enabled) return;
    const parts = combo.toLowerCase().split('+');
    const key = parts[parts.length - 1]!;
    const mod = parts.includes('mod');
    const shift = parts.includes('shift');
    const alt = parts.includes('alt');
    const handler = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (mod !== isMod || shift !== e.shiftKey || alt !== e.altKey) return;
      // Plain-key shortcuts must never eat what the user is typing.
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (typing && !mod && !alt && key.length === 1) return;
      if (
        e.key.toLowerCase() !== key &&
        e.code.toLowerCase() !== `key${key}` &&
        e.code.toLowerCase() !== `digit${key}`
      )
        return;
      e.preventDefault();
      fnRef.current(e);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [combo, enabled]);
}
